import {
  createContext, ReactElement, ReactNode, useContext, useEffect, useState,
} from 'react';
import Cookies from 'js-cookie';
import { useFlagsmith } from '@flagsmith/flagsmith/react';
import { SubscriptionStatus, User } from '../models/user.types';
import { useCurrentUserQuery } from '../services/user/user.queries';
import { useAuth } from './AuthContext';

type Props = {
  hasAccount: boolean
  children: ReactNode
}

type ContextValue = {
  user: User | undefined,
  isActive: boolean,
};

const UserContext = createContext<ContextValue | null>(null);

export function UserProvider({
  hasAccount,
  children,
}: Props): ReactElement {
  const [user, setUser] = useState<User>();
  const [isActive, setIsActive] = useState<boolean>(true);
  const { data: currentUser } = useCurrentUserQuery();
  const { isLoggedIn } = useAuth();
  const flagsmith = useFlagsmith();

  useEffect(() => {
    if (currentUser) {
      const isActiveSubscription = currentUser?.status
        ? [SubscriptionStatus.ACTIVE, SubscriptionStatus.FORCED_SKIPPED,
          SubscriptionStatus.SKIPPED].includes(currentUser?.status) : true;
      setUser(currentUser);
      setIsActive(isActiveSubscription);
    }
  }, [currentUser]);

  useEffect(() => {
    if (window.heap) {
      // load Flagsmith feature flag values based on identity & traits and record them in Heap
      window.heap.onReady(async () => {
        const heapUserId = window.heap.getUserId();

        if (heapUserId && user) {
          await flagsmith.identify(heapUserId, {
            heap_id: heapUserId,
            pc_user_id: user.id,
            is_subscribed: isLoggedIn,
            has_account: hasAccount,
            is_employee: user.isEmployee,
          });
          const allFlags = flagsmith.getAllFlags();
          const flagsWithValues = Object.fromEntries(
            Object.entries(allFlags)
              .map(([key, value]) => ([
                key,
                value.enabled && value.value ? value.value : value.enabled,
              ])),
          );
          window.heap.track('Flagsmith Feature Flags Loaded', flagsWithValues);
        }
      });
    } else {
      // if Heap is unavailable (e.g., 'do_not_sell' users), get Flagsmith identity from cookies
      const flagsmithId = Cookies.get('flagsmith_identity');

      if (flagsmithId && user) {
        flagsmith.identify(flagsmithId, {
          heap_id: null,
          pc_user_id: user.id,
          is_subscribed: isLoggedIn,
          has_account: hasAccount,
          is_employee: user.isEmployee,
        });
      }
    }
  }, [flagsmith, user, isLoggedIn, hasAccount]);

  const contextValue: ContextValue = {
    user,
    isActive,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser(): ContextValue {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within UserProvider');
  return context;
}
